import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// Paleta fija para pintar cada horario con un color distinto y estable en el calendario.
const PALETA = [
  "bg-blue-200 text-blue-900",
  "bg-emerald-200 text-emerald-900",
  "bg-amber-200 text-amber-900",
  "bg-violet-200 text-violet-900",
  "bg-rose-200 text-rose-900",
  "bg-cyan-200 text-cyan-900",
  "bg-lime-200 text-lime-900",
  "bg-orange-200 text-orange-900",
];

function colorFor(horarioId, horarios) {
  const idx = horarios.findIndex((h) => h.id === horarioId);
  return PALETA[idx >= 0 ? idx % PALETA.length : 0];
}

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function monthBounds(date) {
  const desde = new Date(date.getFullYear(), date.getMonth(), 1);
  const hasta = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { fecha_desde: toISO(desde), fecha_hasta: toISO(hasta) };
}

export default function RotacionTurnos() {
  const [empleados, setEmpleados] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [empleadoId, setEmpleadoId] = useState("");
  const [horarioId, setHorarioId] = useState("");
  const [mes, setMes] = useState(new Date());
  const [asignaciones, setAsignaciones] = useState([]);
  const [range, setRange] = useState(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/empleados"), api.get("/horarios")]).then(([e, h]) => {
      setEmpleados(e.data);
      setHorarios(h.data);
      if (e.data.length && !empleadoId) setEmpleadoId(e.data[0].id);
      if (h.data.length && !horarioId) setHorarioId(h.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarAsignaciones = async () => {
    if (!empleadoId) return;
    const { fecha_desde, fecha_hasta } = monthBounds(mes);
    const { data } = await api.get(
      `/asignaciones-horario?empleado_id=${empleadoId}&fecha_desde=${fecha_desde}&fecha_hasta=${fecha_hasta}`
    );
    setAsignaciones(data);
  };
  useEffect(() => { cargarAsignaciones(); setRange(undefined); }, [empleadoId, mes]); // eslint-disable-line react-hooks/exhaustive-deps

  const empleado = empleados.find((e) => e.id === empleadoId);
  const horarioDefault = empleado ? horarios.find((h) => h.id === empleado.horario_id) : null;

  const modifiers = useMemo(() => {
    const porHorario = {};
    for (const a of asignaciones) {
      porHorario[a.horario_id] = porHorario[a.horario_id] || [];
      // new Date("YYYY-MM-DD") se interpreta en UTC; sumar el offset local evita que el
      // calendario pinte el día anterior en husos horarios negativos (México, etc.).
      const d = new Date(a.fecha);
      porHorario[a.horario_id].push(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    }
    return porHorario;
  }, [asignaciones]);

  const modifiersClassNames = useMemo(() => {
    const cls = {};
    for (const horarioIdKey of Object.keys(modifiers)) {
      cls[horarioIdKey] = colorFor(horarioIdKey, horarios) + " rounded-md font-bold";
    }
    return cls;
  }, [modifiers, horarios]);

  const asignar = async () => {
    if (!range?.from || !empleadoId || !horarioId) return;
    setBusy(true);
    try {
      await api.post("/asignaciones-horario", {
        empleado_id: empleadoId,
        horario_id: horarioId,
        fecha_desde: toISO(range.from),
        fecha_hasta: toISO(range.to || range.from),
      });
      toast.success("Turno asignado");
      setRange(undefined);
      cargarAsignaciones();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const quitar = async () => {
    if (!range?.from || !empleadoId) return;
    setBusy(true);
    try {
      await api.delete(
        `/asignaciones-horario?empleado_id=${empleadoId}&fecha_desde=${toISO(range.from)}&fecha_hasta=${toISO(range.to || range.from)}`
      );
      toast.success("Asignación quitada de esos días");
      setRange(undefined);
      cargarAsignaciones();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-6">
      <div className="space-y-4">
        <div className="space-y-1">
          <Label className="label-uppercase">Empleado</Label>
          <Select value={empleadoId} onValueChange={setEmpleadoId}>
            <SelectTrigger data-testid="rot-empleado-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="text-xs text-slate-500">
          Horario por defecto: <strong>{horarioDefault?.nombre || "sin asignar"}</strong>. Se usa en los días
          sin un turno específico asignado abajo.
        </div>

        <div className="space-y-1">
          <Label className="label-uppercase">Selecciona un rango de días en el calendario y asígnales un horario</Label>
        </div>
        <div className="space-y-1">
          <Label className="label-uppercase">Horario a asignar</Label>
          <Select value={horarioId} onValueChange={setHorarioId}>
            <SelectTrigger data-testid="rot-horario-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {horarios.map((h) => <SelectItem key={h.id} value={h.id}>{h.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button onClick={asignar} disabled={busy || !range?.from} className="bg-brand hover:bg-brand-hover" data-testid="rot-asignar-btn">
            Asignar
          </Button>
          <Button onClick={quitar} disabled={busy || !range?.from} variant="outline" data-testid="rot-quitar-btn">
            Quitar
          </Button>
        </div>

        {horarios.length > 0 && (
          <div className="pt-3 border-t border-slate-100">
            <div className="label-uppercase mb-2">Leyenda</div>
            <div className="space-y-1">
              {horarios.map((h) => (
                <div key={h.id} className="flex items-center gap-2 text-xs">
                  <span className={`w-4 h-4 rounded ${colorFor(h.id, horarios).split(" ")[0]}`} />
                  {h.nombre}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 flex justify-center">
        <Calendar
          mode="range"
          selected={range}
          onSelect={setRange}
          month={mes}
          onMonthChange={setMes}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          data-testid="rot-calendar"
        />
      </div>
    </div>
  );
}
